<template>
    <CqModal
        :showModal="show"
        @closeModal="closeModal"
        :preventClosing="isSaving"
        :heading="`Dienst${openedForProposal ? 'voorstel' : ''} bewerken`"
        maxWidth="min(100rem, 90vw)"
    >
        <!-- only render when shown, to force reset all states and values when a new deployment is viewed -->
        <!-- for example: the filter of CqSelectInput is not reset when reopening the modal, if you do not do this -->
        <template v-if="show">
            <InfoGrid>
                <InfoHeader>Werknemer</InfoHeader><span>{{ dataModel.name }}</span>
                <InfoHeader>Datum</InfoHeader><span>{{ dataModel.date }}</span>
                <CqSelectInput noMargin v-if="resource" class="infoGridInformationWithoutHeader" label="Rooster" v-model="dataModel.rosterId" @update:modelValue="changedRoster(Number($event))" :options="filteredRosters" />
                <CqSelectInput noMargin class="infoGridInformationWithoutHeader" label="Dienst" v-model="dataModel.shiftId" @update:modelValue="changeShift(Number($event))" :options="filterShifts" />
                <template v-if="shifts[dataModel.shiftId] && shifts[dataModel.shiftId].template!.legend">
                    <!-- because of the above, this information _which is about the shift_ is now not positioned next to the shift anymore -->
                    <InfoHeader>Legenda</InfoHeader><span>{{ shifts[dataModel.shiftId].template!.legend }}</span>
                </template>
                <template v-if="isDeploymentFromOtherCompany">
                    <InfoHeader>Toestand</InfoHeader><span>{{ getDefaultDeploymentState().caption }}</span>
                </template>
                <CqSelectInput v-else label="Toestand" class="infoGridInformationWithoutHeader" noMargin v-model="dataModel.stateId" :options="stateOptions" @update:modelValue="changeDeploymentState(Number($event))" />
                <CqTextAreaInput label="Opmerking" class="infoGridInformationWithoutHeader" noMargin maxlength="50" v-model.trim="dataComment" />
                <div v-if="deploymentHasPositionAssignments" class="infoGridInformationWithoutHeader">Deze dienst is gekoppeld aan een positie</div>
            </InfoGrid>
            <WishInformation :activeWish="activeWish" :date="date" :shifts="shifts" />

            <div class="specifications" v-if="specify">
                <div class="specificationHeader">
                    <h3>Specificeer dienst ({{ specifications.length }})</h3>
                    <CqButton class="actionColumn" primary icon="plus" label="Voeg toe" :disabled="hasLastSpecificationStartingBeforeSecondToLast" margin="no-top-bottom" @click="addSpecification" />
                </div>
                <span v-if="specifications.length == 0">Er zijn geen specificaties voor deze dienst.</span>
                <div class="specificationGrid" :class="{'with-projects': projects}">
                    <template v-for="(specification, index) in specifications" :key="index">
                        <CqSelectInput
                            label="Van"
                            :options="getDateTimeSpecificationOptionsForBegin(index)"
                            :modelValue="specification.begin.toISOString()"
                            @update:modelValue="updateSpecificationBeginDateAndPreviousEndDate(specification, index, $event as string)"
                        />
                        <CqSelectInput
                            label="Tot"
                            :options="getDateTimeSpecificationOptionsForEnd(index)"
                            :modelValue="specification.end.toISOString()"
                            @update:modelValue="specification.end = new Date($event as string)"
                            :readonly="index != specificationsMaxIndex"
                        />
                        <CqSelectInput label="Type" :options="typeOptions" v-model="specification.typeId" />
                        <CqSelectInput class="stateColumn" label="Toestand" :options="stateOptions" v-model="specification.stateId" />
                        <div class="leaveColumn" v-if="leaveTypeFromState(specification.stateId) && shouldShowLeaveForType(specification.typeId)">
                            <CqSelectInput
                                v-if="isProposalSpecification(specification)"
                                label="Verlofregeling"
                                :options="filterLeaveOptions(specification.leaveKindId, leaveOptions[leaveTypeFromState(specification.stateId)!])"
                                v-model="specification.leaveKindId"
                            />
                            <CqSelectInput
                                v-else
                                label="Verlofregeling"
                                :options="filterLeaveOptions(specification.leaveKind, leaveOptions[leaveTypeFromState(specification.stateId)!])"
                                :modelValue="specification.leaveKind ?? null"
                                @update:modelValue="specification.leaveKind = $event === null ? undefined : $event"
                            />
                        </div>
                        <div class="projectColumn" v-if="projects">
                            <CqSelectInput label="Project" :options="projectOptions" v-model="specification.projectId" @update:modelValue="specification.activityId = null" />
                        </div>
                        <div v-if="projects">
                            <CqSelectInput label="Activiteit" :options="getProjectActivitiesByProjectId(specification.projectId)" v-model="specification.activityId" />
                        </div>
                        <CqButton class="actionColumn" dangerous icon="remove" margin="no-top-bottom" @click="removeSpecification(index)" />
                    </template>
                </div>
                <div class="error-container" v-if="!isValid">
                    <h3>Ongeldige specificaties</h3>
                    <p>De dienst heeft ongeldige specificaties en kan niet opgeslagen worden.</p>
                    <ul class="errors">
                        <li v-for="error in validationErrors" :key="error">{{ error }}</li>
                    </ul>
                </div>
            </div>
        </template>
        <div class="position-assignment-change-message" v-if="!positionAssignmentIsKept">Let op: de toegewezen positie wordt ontkoppeld.</div>
        <CqButtonBar>
            <CqButton @click="specify = !specify" :label="specify ? 'Niet specificeren' : 'Specificeren'" />
            <CqButton @click="closeModal" label="Annuleren" :disabled="isSaving" />
            <CqButton @click="save" primary :disabled="!isValid || isSaving" label="Opslaan" icon="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal, CqSelectInput, CqTextAreaInput, NumberSelectInputOption, StringSelectInputOption, NullSelectInputOption} from '@cquential/ui';
    import WishInformation from '@/components/modals/WishInformation.vue';
    import {ShiftStateUpdateModalModel} from '@/components/modals/types';
    import {RosterSelectOption, PermissionsForRoster} from '@/types/types';
    import {
        PersonalPlanningWish,
        Organisation,
        DeploymentSpecification,
        LeaveKind,
        Template,
        OrganisationDeploymentState,
        DeploymentType,
        TemplateSpecification,
        Shift,
        Project,
        DeploymentProposal,
        DeploymentProposalSpecification,
        Company,
        Deployment,
        LeaveType,
        DeploymentStateNumber,
    } from '@cquential/api';
    import {OrganisationUtil} from '@/util/organisation';
    import Dates, {DateCompare, DateUtilTimeFormatSecondsMode} from '@cquential/date-utils';
    import cloneDeep from 'lodash/cloneDeep';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {Component, Vue, Prop, Watch} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqSelectInput,
            CqTextAreaInput,
            WishInformation,
            InfoGrid,
            InfoHeader,
        },
        emits: [
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:deploymentProposal',
            'update:deploymentProposal',
            'delete:deploymentProposal',
        ],
    })
    export default class ShiftStateUpdateModal extends Vue {
        private static instance: ShiftStateUpdateModal | null = null;

        @Prop({required: true}) public rosters!: RosterSelectOption[];
        @Prop({type: Number, required: true}) public rosterId!: number;
        @Prop({required: true}) public organisations!: Record<string, Organisation>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public shiftIdsPerRoster!: Record<number, number[]>;
        @Prop({required: true}) public defaultShiftIdPerRoster!: Record<number, number>;
        @Prop() public projects?: Project[];
        @Prop({required: true}) public permissionsPerRoster!: Record<number, PermissionsForRoster>;
        @Prop({required: true}) public leaveKinds!: LeaveKind[];

        private show: boolean = false;
        private isSaving: boolean = false;
        private dateTimeSpecificationOptionsByTime: Record<string, StringSelectInputOption> = {};
        private specify: boolean = false;
        private openedForProposal: boolean = false;
        private currentOrganisationsId: string = '';
        private stateOptions: NumberSelectInputOption[] = [];
        private typeOptions: NumberSelectInputOption[] = [];
        private specifications: (DeploymentSpecification | DeploymentProposalSpecification)[] = [];
        private projectOptions: [NullSelectInputOption, ...NumberSelectInputOption[]] = [{
            label: '- Selecteer -',
            value: null,
        }];

        private activityOptionsPerProject: Record<number, NumberSelectInputOption[]> = {};
        private latestDateTime: Date | null = null;
        private resource: ResourceWithKeyedByDateObjects | null = null;
        private company: Company | null = null;
        private deployment: Deployment | DeploymentProposal | null = null;
        private date: Date = new Date();
        private dataModel: ShiftStateUpdateModalModel = {
            name: '',
            date: '',
            rosterId: 0,
            stateId: 1,
            publicComment: '',
            shiftId: 0,
            deploymentId: 0,
            dateObject: new Date(),
        };

        @Watch('specifications', {deep: true})
        public onSpecificationsChange(): void {
            this.onChangeSpecifications();
        }

        @Watch('dataModel.stateId')
        public onStateIdChange(): void {
            this.onChangeState();
        }

        public mounted(): void {
            ShiftStateUpdateModal.instance = this;
        }

        public beforeUnmount(): void {
            ShiftStateUpdateModal.instance = null;
        }

        public get dataComment(): string {
            return this.dataModel.publicComment ?? '';
        }

        public set dataComment(value: string) {
            this.dataModel.publicComment = value === '' ? null : value;
        }

        public get filteredRosters(): NumberSelectInputOption[] {
            return this.rosters.filter((roster: RosterSelectOption): boolean => {
                if (roster.data.beginDate && roster.data.beginDate > this.date) {
                    return false;
                }

                if (!this.overlappingFunction(roster)) {
                    return false;
                }

                const rosterIsProposed: boolean = roster.data.proposedUntil !== null && roster.data.proposedUntil >= this.date;

                return roster.data.id in this.permissionsPerRoster
                    && (
                        this.permissionsPerRoster[roster.data.id][UserPermissions.PutOwnRosterPeopleOnRoster]
                        || this.permissionsPerRoster[roster.data.id][rosterIsProposed ? UserPermissions.EditPublishedPeriod : UserPermissions.EditConstructionPeriod]
                    );
            }).map((roster: RosterSelectOption): NumberSelectInputOption => {
                return {
                    value: roster.data.id,
                    label: roster.data.name,
                };
            });
        }

        public get activeWish(): PersonalPlanningWish | undefined {
            if (this.resource === null || !this.resource.personalPlanningWishes) {
                return undefined;
            }

            for (const wish of this.resource.personalPlanningWishes) {
                if ((wish.recursWeekly && (wish.recursOnDay % 7) === Dates.Utils.getDay(this.date)) || (wish.date && wish.date.valueOf() === this.date.valueOf())) {
                    return wish;
                }
            }

            return undefined;
        }

        public get filterShifts(): NumberSelectInputOption[] {
            const filteredShifts: NumberSelectInputOption[] = [];

            if (!(this.dataModel.rosterId in this.shiftIdsPerRoster) || !this.date) {
                return [];
            }

            for (const shiftId of this.shiftIdsPerRoster[this.dataModel.rosterId]) {
                if (this.date >= this.shifts[shiftId].beginDate && this.date <= this.shifts[shiftId].endDate) {
                    filteredShifts.push({
                        label: this.shifts[shiftId].template!.code,
                        value: shiftId,
                    });
                }
            }
            filteredShifts.sort((a: NumberSelectInputOption, b: NumberSelectInputOption): number => a.label.localeCompare(b.label));

            return filteredShifts;
        }

        public get isDeploymentFromOtherCompany(): boolean {
            return this.deployment !== null && !this.isProposal(this.deployment) && this.deployment.companyId !== null;
        }

        public get organisation(): Organisation | undefined {
            return this.organisations[this.currentOrganisationsId];
        }

        public get forcedPrefix(): string | null {
            if (!this.organisation) {
                return null;
            }

            for (const stateNumber in this.organisation.organisationDeploymentStates) {
                if (stateNumber === this.dataModel.stateId.toString()) {
                    return this.organisation.organisationDeploymentStates[stateNumber].comment;
                }
            }

            return null;
        }

        public get fullComment(): string | null {
            if (this.forcedPrefix === null || this.dataModel.publicComment === this.forcedPrefix) {
                return this.dataModel.publicComment;
            }

            if (this.dataModel.publicComment === null || this.dataModel.publicComment.length === 0) {
                return this.forcedPrefix;
            }

            if (this.dataModel.publicComment.substr(0, this.forcedPrefix.length + 1) === (this.forcedPrefix + ' ')) {
                return this.dataModel.publicComment;
            }

            return this.forcedPrefix + ' ' + this.dataModel.publicComment;
        }

        public get deploymentHasPositionAssignments(): boolean {
            return !!this.deployment && !this.isProposal(this.deployment) && !!this.deployment.positionAssignmentSetGroupPositionDeployments && this.deployment.positionAssignmentSetGroupPositionDeployments.length > 0;
        }

        public get positionAssignmentIsKept(): boolean {
            if (!this.deploymentHasPositionAssignments) {
                return true;
            }

            if (!this.deployment || this.isProposal(this.deployment) || !this.deployment.positionAssignmentSetGroupPositionDeployments) {
                return true;
            }

            if (this.deployment && this.shifts[this.deployment.shiftId].rosterId !== this.dataModel.rosterId) {
                return false;
            }

            let overlappingStart: boolean = false;
            let overlappingEnd: boolean = false;
            const minBegin: Date = Dates.Utils.min(...this.specifications.map((specification: DeploymentSpecification | DeploymentProposalSpecification): Date => specification.begin));
            const maxEnd: Date = Dates.Utils.max(...this.specifications.map((specification: DeploymentSpecification | DeploymentProposalSpecification): Date => specification.end));
            for (const paDeployment of this.deployment.positionAssignmentSetGroupPositionDeployments) {
                const paBeginDate: Date = Dates.Utils.setTimeOnDate(paDeployment.date, paDeployment.beginTime);
                const paEndDate: Date = Dates.Utils.setTimeOnDate(paDeployment.date, paDeployment.endTime);
                if (paBeginDate >= minBegin && paBeginDate < maxEnd) {
                    overlappingStart = true;
                }
                if (paEndDate > minBegin && paEndDate <= maxEnd) {
                    overlappingEnd = true;
                }
            }

            return overlappingStart && overlappingEnd;
        }

        public get isValid(): boolean {
            return this.validationErrors.length === 0;
        }

        public get validationErrors(): string[] {
            const errors: string[] = [];

            if (this.specify) {
                for (let i: number = 0; i < this.specifications.length; i++) {
                    for (let j: number = i + 1; j < this.specifications.length; j++) {
                        if (this.specifications[i].begin < this.specifications[j].end && this.specifications[i].end > this.specifications[j].begin) {
                            errors.push(`De ${i + 1}e specificatie overlapt met de ${j + 1}e.`);
                        }
                    }

                    if (this.specifications[i].begin.getTime() % (this.organisation!.timePrecision * 60000) !== 0) {
                        errors.push(`De begintijd van ${i + 1}e specificatie voldoet niet aan de vereiste tijdprecisie (${this.organisation!.timePrecision} minuten).`);
                    }
                    if (this.specifications[i].end.getTime() % (this.organisation!.timePrecision * 60000) !== 0) {
                        errors.push(`De eindtijd van ${i + 1}e specificatie voldoet niet aan de vereiste tijdprecisie (${this.organisation!.timePrecision} minuten).`);
                    }
                    if (this.specifications[i].end <= this.specifications[i].begin) {
                        errors.push(`De ${i + 1}e specificatie moet eindigen nadat hij begint.`);
                    }
                }
            }

            return errors;
        }

        public get specificationsMaxIndex(): number {
            return this.specifications.length - 1;
        }

        public get leaveOptions(): Record<number, NumberSelectInputOption[]> {
            const result: Record<number, NumberSelectInputOption[]> = {};
            for (const type in LeaveType) {
                const intType: number = Number(type);
                if (!isNaN(intType)) {
                    result[intType] ??= [];
                }
            }

            for (const leaveKind of this.leaveKinds) {
                if (leaveKind.timeType && leaveKind.enabledYears.includes(Dates.Utils.getYear(this.date))) {
                    result[leaveKind.timeType].push({
                        label: `${leaveKind.code} ${leaveKind.description}`,
                        value: leaveKind.id,
                    });
                }
            }

            return result;
        }

        public get hasLastSpecificationStartingBeforeSecondToLast(): boolean {
            const specificationLength: number = this.specifications.length;

            return specificationLength >= 2 && this.specifications[specificationLength - 1].begin.getTime() < this.specifications[specificationLength - 2].begin.getTime();
        }

        public get dateTimeSpecificationOptions(): StringSelectInputOption[] {
            return Object.values(this.dateTimeSpecificationOptionsByTime);
        }

        public openModal(openResource: ResourceWithKeyedByDateObjects | undefined, openCompany: Company | undefined, openDeployment: Deployment | DeploymentProposal | false, openDate: Date, shouldBeProposal: boolean = false): void {
            if (openDeployment && this.isProposal(openDeployment) && (openDeployment.refused || openDeployment.deploymentId !== null)) {
                throw new Error('Cannot edit accepted or refused proposal.');
            }

            /** @see KeyboardDeploymentAutocomplete.disable() */
            // @ts-expect-error TS2339
            KeyboardDeploymentAutocomplete.__vfdConstructor.disable();
            this.setCurrentOrganisationsId(this.rosterId);
            this.latestDateTime = null;
            this.resource = openResource ? openResource : null;
            this.company = openCompany ? openCompany : null;
            this.deployment = openDeployment ? openDeployment : null;
            this.specify = !!(this.deployment && this.deployment.specified);
            this.openedForProposal = shouldBeProposal;
            this.show = true;
            this.isSaving = false;
            this.date = new Date(openDate);

            let initialRosterId: number = 0;
            if (openDeployment) {
                if (this.shifts[openDeployment.shiftId] && this.shifts[openDeployment.shiftId].rosterId) {
                    initialRosterId = this.shifts[openDeployment.shiftId].rosterId;
                }
            } else if (this.rosterId) {
                initialRosterId = this.rosterId;
            }

            this.dataModel = {
                name: ResourceUtil.displayName(openResource) ?? openCompany?.name ?? '',
                date: Dates.Formatter.formatDateLong(openDate),
                rosterId: initialRosterId,
                stateId: (openDeployment && openDeployment.stateId) ? openDeployment.stateId : OrganisationUtil.getDefaultState(this.organisation!.organisationDeploymentStates),
                publicComment: openDeployment ? openDeployment.publicComment : '',
                deploymentId: openDeployment ? openDeployment.id : undefined,
                shiftId: openDeployment ? openDeployment.shiftId : this.defaultShiftIdPerRoster[this.rosterId],
                resourceId: openResource ? openResource.id : undefined,
                companyId: openCompany ? openCompany.id : (openDeployment && !this.isProposal(openDeployment) && openDeployment.companyId ? openDeployment.companyId : undefined),
                companyEmployeeId: openDeployment && !this.isProposal(openDeployment) && openDeployment.companyEmployeeId ? openDeployment.companyEmployeeId : undefined,
                companyEmployee: this.dataModel.companyEmployee ? this.dataModel.companyEmployee : undefined,
                dateObject: new Date(openDate),
            };

            this.buildStateOptions();
            this.buildTypeOptions();
            this.buildSpecifications();
            this.sortSpecifications();
            this.buildDateTimeSpecificationOptions();
            this.sanitizePublicComment();

            if (this.projects) {
                this.buildProjectsAndActivities();
            }
        }

        public isProposalSpecification(specification: DeploymentSpecification | DeploymentProposalSpecification): specification is DeploymentProposalSpecification {
            return 'leaveKindId' in specification;
        }

        public isProposal(deployment: Deployment | DeploymentProposal): deployment is DeploymentProposal {
            return !('companyId' in deployment);
        }

        public overlappingFunction(singleRoster: RosterSelectOption): boolean {
            if (!this.resource) {
                return false;
            }
            if (singleRoster.value === this.rosterId) {
                return true;
            } else {
                for (const index in this.resource.functions) {
                    if (singleRoster.data.allowedFunctions && singleRoster.data.allowedFunctions[this.resource.functions[Number(index)].id!]) {
                        return true;
                    }
                }
            }

            return false;
        }

        public buildDateTimeSpecificationOptions(): void {
            const optionsKeyedByDateString: Record<string, StringSelectInputOption> = {};
            const localDate: Date = new Date(this.date);
            const end: Date = Dates.Utils.addDays(this.date, 2);

            let precision: number = this.organisation!.timePrecision;

            if (precision < 1) {
                precision = 1;
            }

            for (let i: number = 1; localDate < end; i++) {
                optionsKeyedByDateString[localDate.toISOString()] = this.makeDateSelectOption(localDate, i * precision);
                localDate.setMinutes(localDate.getMinutes() + precision);
            }

            for (const specification of this.specifications) {
                if (!(specification.begin.toISOString() in optionsKeyedByDateString)) {
                    optionsKeyedByDateString[specification.begin.toISOString()] = this.makeDateSelectOption(specification.begin, undefined, true);
                }
                if (!(specification.end.toISOString() in optionsKeyedByDateString)) {
                    optionsKeyedByDateString[specification.end.toISOString()] = this.makeDateSelectOption(specification.end, undefined, true);
                }
            }

            this.dateTimeSpecificationOptionsByTime = optionsKeyedByDateString;
        }

        public getDateTimeSpecificationOptionsForBegin(index: number): StringSelectInputOption[] {
            return this.getDateTimeSpecificationOptions(this.specifications[index - 1]?.begin ?? null, this.specifications[index + 1]?.begin ?? null);
        }

        public getDateTimeSpecificationOptionsForEnd(index: number): StringSelectInputOption[] {
            return index < this.specificationsMaxIndex
                ? [this.dateTimeSpecificationOptionsByTime[this.specifications[index].end.toISOString()]]
                : this.getDateTimeSpecificationOptions(this.specifications[index].begin, null);
        }

        public getDateTimeSpecificationOptions(lastExcludedTimeAtBegin: Date | null, firstExcludedTimeAtEnd: Date | null): StringSelectInputOption[] {
            if (lastExcludedTimeAtBegin === null && firstExcludedTimeAtEnd === null) {
                return this.dateTimeSpecificationOptions;
            }

            const lastExcludedTimeAtBeginISOString: string = lastExcludedTimeAtBegin?.toISOString() ?? '0'; // Implicit minimal date
            const firstExcludedTimeAtEndISOString: string = firstExcludedTimeAtEnd?.toISOString() ?? '9999-12-31T99:99:99.999Z';

            return this.dateTimeSpecificationOptions.map((option: StringSelectInputOption): StringSelectInputOption => {
                if (!option.disabled && (option.value <= lastExcludedTimeAtBeginISOString || option.value >= firstExcludedTimeAtEndISOString)) {
                    return {...option, disabled: true};
                }

                return option;
            });
        }

        public makeDateSelectOption(selectDate: Date, minutesSinceDeploymentStart?: number, disabled: boolean = false): StringSelectInputOption {
            if (typeof minutesSinceDeploymentStart === 'undefined') {
                minutesSinceDeploymentStart = (selectDate.getTime() - this.date.getTime()) / 60000;
            }

            return {
                label: ((minutesSinceDeploymentStart > 1440) ? '+1 ' : '') + Dates.Formatter.formatTime(selectDate, DateUtilTimeFormatSecondsMode.ShowSecondsIfNonZero),
                value: selectDate.toISOString(),
                disabled,
            };
        }

        public buildStateOptions(): void {
            this.stateOptions = [];

            const template: Template = this.shifts[this.dataModel.shiftId].template!;
            if (this.isDeploymentFromOtherCompany || (!template.hasAllStates && !template.hasLeaveState)) {
                this.buildDefaultStateOption();

                return;
            }

            const organisationDeploymentStates: Record<number, OrganisationDeploymentState> = this.organisation!.organisationDeploymentStates;

            for (const stateId in organisationDeploymentStates) {
                if (organisationDeploymentStates[stateId].beginDate <= this.date && organisationDeploymentStates[stateId].endDate >= this.date) {
                    this.stateOptions.push({
                        label: organisationDeploymentStates[stateId].caption,
                        value: organisationDeploymentStates[stateId].deploymentStateId,
                    });
                }
            }
        }

        public buildDefaultStateOption(): void {
            const defaultState: OrganisationDeploymentState = this.getDefaultDeploymentState();
            this.stateOptions.push({
                label: defaultState.caption,
                value: defaultState.deploymentStateId,
            });
        }

        public buildTypeOptions(): void {
            this.typeOptions = [];
            const organisationDeploymentTypes: Record<number, DeploymentType> = this.organisation!.organisationDeploymentTypes;
            for (const typeId in organisationDeploymentTypes) {
                this.typeOptions.push({
                    label: organisationDeploymentTypes[typeId].name,
                    value: organisationDeploymentTypes[typeId].deploymentTypeId,
                });
            }
        }

        public buildProjectsAndActivities(): void {
            this.projectOptions = [{
                label: '- Selecteer -',
                value: null,
            }];
            this.activityOptionsPerProject = {};

            const compareDate: DateCompare = Dates.compare(this.date);

            for (const project of this.projects ?? []) {
                if (!compareDate.isBetweenDatesInclusive(project.beginDate, project.endDate ?? this.date)) {
                    continue;
                }
                this.projectOptions.push({
                    label: `${project.value} - ${project.description}`,
                    value: project.id,
                });

                const activities: NumberSelectInputOption[] = [];
                if (project.activities) {
                    for (const activity of project.activities) {
                        if (!compareDate.isBetweenDatesInclusive(activity.beginDate, activity.endDate ?? this.date)) {
                            continue;
                        }
                        activities.push({
                            label: `${activity.value} - ${activity.description}`,
                            value: activity.id,
                        });
                    }
                }
                this.activityOptionsPerProject[project.id] = activities;
            }
        }

        public getProjectActivitiesByProjectId(projectId?: number | null): [NullSelectInputOption, ...NumberSelectInputOption[]] {
            if (projectId && this.activityOptionsPerProject[projectId]?.length > 0) {
                return [{
                    value: null,
                    label: 'Selecteer activiteit',
                }, ...this.activityOptionsPerProject[projectId]];
            }

            return [{
                value: null,
                label: 'Selecteer activiteit',
            }];
        }

        public setStateToDefaultState(): void {
            const defaultState: OrganisationDeploymentState = this.getDefaultDeploymentState();
            this.stateOptions = [{
                label: defaultState.caption,
                value: defaultState.deploymentStateId,
            }];
            this.dataModel.stateId = 1;
        }

        public getDefaultDeploymentState(): OrganisationDeploymentState {
            return this.organisation!.organisationDeploymentStates[OrganisationUtil.getDefaultState(this.organisation!.organisationDeploymentStates)];
        }

        public changedRoster(rosterId: number): void {
            if (this.filterShifts[0].value) {
                this.dataModel.shiftId = Number(this.filterShifts[0].value);
            } else {
                this.dataModel.shiftId = 0;
            }
            this.setCurrentOrganisationsId(rosterId);
            this.buildStateOptions();
            this.setStateToDefaultState();
            this.changeShift(this.dataModel.shiftId);
        }

        public setCurrentOrganisationsId(rosterId: number): void {
            for (const roster of this.rosters) {
                if (roster.value === rosterId) {
                    this.currentOrganisationsId = roster.data.organisationId;
                    break;
                }
            }
        }

        public changeShift(shiftId: number): void {
            this.buildStateOptions();
            if (this.openedForProposal) {
                this.specifications = this.shifts[shiftId].template!.specifications!.map(this.createProposalSpecificationFromTemplateSpecification);
            } else {
                this.specifications = this.shifts[shiftId].template!.specifications!.map(this.createSpecificationFromTemplateSpecification);
            }
            this.sortSpecifications();
        }

        public buildSpecifications(): void {
            this.specifications = [];
            if (this.deployment && this.deployment.specifications) {
                if (this.deployment.specifications.length > 0) {
                    for (const spec of this.deployment.specifications) {
                        this.specifications.push(cloneDeep(spec));
                    }
                } else if (this.openedForProposal && !this.deployment.specified) {
                    this.changeShift(this.dataModel.shiftId);
                }
            } else {
                if (this.deployment) {
                    this.changeShift(this.dataModel.shiftId);
                }
            }
        }

        public createSpecificationFromTemplateSpecification(specification: TemplateSpecification): DeploymentSpecification {
            const begin: Date = new Date(this.dataModel.dateObject);
            begin.setSeconds(begin.getSeconds() + Dates.Time.parse(specification.begin));

            const end: Date = new Date(this.dataModel.dateObject);
            end.setSeconds(begin.getSeconds() + Dates.Time.parse(specification.end));

            return {
                begin,
                end,
                stateId: this.deployment ? this.deployment.stateId : Number(this.stateOptions[0].value),
                typeId: specification.typeId,
                costCenterId: null,
                leaveKind: null,
                projectId: null,
                activityId: null,
            };
        }

        public createProposalSpecificationFromTemplateSpecification(specification: TemplateSpecification): DeploymentProposalSpecification {
            const begin: Date = new Date(this.dataModel.dateObject);
            begin.setSeconds(begin.getSeconds() + Dates.Time.parse(specification.begin));

            const end: Date = new Date(this.dataModel.dateObject);
            end.setSeconds(begin.getSeconds() + Dates.Time.parse(specification.end));

            return {
                begin,
                end,
                stateId: this.deployment ? this.deployment.stateId : Number(this.stateOptions[0].value),
                typeId: specification.typeId,
                leaveKindId: null,
                projectId: null,
                activityId: null,
            };
        }

        public leaveTypeFromState(stateNumber: number): LeaveType | null {
            if (!this.organisation) {
                return null;
            }

            const state: OrganisationDeploymentState = this.organisation.organisationDeploymentStates[stateNumber];
            if (state.properties.normalLeave) {
                return LeaveType.Leave;
            } else if (state.properties.compensationLeave) {
                return LeaveType.CompensatoryLeave;
            }

            return null;
        }

        public shouldShowLeaveForType(typeNumber: number): boolean {
            if (!this.organisation) {
                // prefer false positives over false negatives
                return true;
            }

            const type: DeploymentType = this.organisation.organisationDeploymentTypes[typeNumber];

            return type.timeMultiplier > 0;
        }

        public updateSpecificationBeginDateAndPreviousEndDate(specification: DeploymentSpecification | DeploymentProposalSpecification, index: number, value: string): void {
            const newDate: Date = new Date(value);

            specification.begin = newDate;
            if (index > 0) {
                if (newDate > this.specifications[index - 1].begin) {
                    this.specifications[index - 1].end = newDate;
                } else {
                    if (newDate <= this.specifications[0].begin) {
                        specification.end = new Date(this.specifications[0].begin);
                    }
                    if (this.deployment && this.deployment.specifications) {
                        this.specifications[index - 1].end = new Date(this.deployment.specifications[index - 1].end);
                    }
                }
            }
        }

        public addSpecification(): void {
            if (this.latestDateTime === null) {
                this.latestDateTime = this.date;
            }

            let leaveKind: number | null = null;
            const leaveType: LeaveType | null = this.leaveTypeFromState(this.dataModel.stateId);
            if (leaveType && this.leaveOptions[leaveType].length) {
                leaveKind = Number(this.leaveOptions[leaveType][0].value);
            }

            const endDate: Date = new Date(this.latestDateTime);
            endDate.setMinutes(endDate.getMinutes() + (this.organisation ? this.organisation.timePrecision : 15));

            if (this.openedForProposal) {
                const specification: DeploymentProposalSpecification = {
                    begin: this.latestDateTime,
                    end: endDate,
                    stateId: Number(this.stateOptions[0].value),
                    typeId: Number(this.typeOptions[0].value),
                    leaveKindId: leaveKind ?? null,
                    projectId: null,
                    activityId: null,
                };
                this.specifications.push(specification);
            } else {
                const specification: DeploymentSpecification = {
                    begin: this.latestDateTime,
                    end: endDate,
                    stateId: Number(this.stateOptions[0].value),
                    typeId: Number(this.typeOptions[0].value),
                    leaveKind,
                    costCenterId: null,
                    projectId: null,
                    activityId: null,
                };
                this.specifications.push(specification);
            }
        }

        public removeSpecification(index: number): void {
            if (this.specifications[index + 1] && index > 0) {
                this.specifications[index - 1].end = new Date(this.specifications[index + 1].begin);
            }
            this.specifications.splice(index, 1);
        }

        public validateSpecifications(): void {
            for (const spec in this.specifications) {
                if (this.specifications[spec].begin && this.specifications[spec].begin !== null) {
                    this.latestDateTime = new Date(this.specifications[spec].begin);
                }
                if (this.specifications[spec].end && this.specifications[spec].end !== null) {
                    this.latestDateTime = new Date(this.specifications[spec].end);
                }
            }
        }

        public save(): void {
            this.isSaving = true;

            this.sortSpecifications();
            this.ensureNoTimeGapsBetweenSpecificationsExist();

            if (this.openedForProposal) {
                if (!this.deployment || !this.deployment.id) {
                    this.saveNewDeploymentProposal();
                } else {
                    this.saveUpdatedDeploymentProposal();
                }
            } else {
                if (!this.deployment || !this.deployment.id) {
                    this.saveNewDeployment();
                } else {
                    this.saveUpdatedDeployment();
                }
            }

            this.closeModal();
        }

        public ensureNoTimeGapsBetweenSpecificationsExist(): void {
            for (let i: number = 1; i < this.specifications.length; i++) {
                if (this.specifications[i].begin < this.specifications[i - 1].end) {
                    this.specifications[i].begin = new Date(this.specifications[i - 1].end);
                }
            }
        }

        public sortSpecifications(): void {
            this.specifications.sort((a: DeploymentSpecification | DeploymentProposalSpecification, b: DeploymentSpecification | DeploymentProposalSpecification): number => {
                return a.begin.getTime() - b.begin.getTime();
            });
        }

        public saveUpdatedDeployment(): void {
            if (!this.resource && !this.company) {
                throw new Error('Deployment and resource or company need to be set, before calling saveState().');
            }

            const deploymentToSave: Deployment = {
                id: this.dataModel.deploymentId!,
                publicComment: this.fullComment,
                date: this.dataModel.dateObject,
                shiftId: this.dataModel.shiftId,
                stateId: this.dataModel.stateId,
                resourceId: this.dataModel.resourceId ? this.dataModel.resourceId : null,
                companyId: this.dataModel.companyId ? this.dataModel.companyId : null,
                companyEmployeeId: this.dataModel.companyEmployeeId ? this.dataModel.companyEmployeeId : null,
                companyEmployee: this.dataModel.companyEmployee ? this.dataModel.companyEmployee : undefined,
                specified: this.specify,
            };

            deploymentToSave.specifications = this.specifications as DeploymentSpecification[];
            deploymentToSave.specified = this.specify;

            if (this.isUnchangedDefaultDeployment(deploymentToSave)) {
                this.$emit('delete:deployment', deploymentToSave);
            } else {
                this.$emit('update:deployment', deploymentToSave);
            }
        }

        public isUnchangedDefaultDeployment(deploymentToSave: Deployment | DeploymentProposal): boolean {
            return this.shifts[this.dataModel.shiftId].template!.isDefault
                && deploymentToSave.stateId === DeploymentStateNumber.Normal
                && !deploymentToSave.specified
                && (deploymentToSave.publicComment === null || deploymentToSave.publicComment.length === 0);
        }

        public saveNewDeployment(): void {
            const deployment: Deployment = {
                publicComment: this.fullComment,
                date: this.dataModel.dateObject,
                shiftId: this.dataModel.shiftId,
                stateId: this.dataModel.stateId,
                resourceId: this.dataModel.resourceId ? this.dataModel.resourceId : null,
                companyId: this.dataModel.companyId ? this.dataModel.companyId : null,
                companyEmployeeId: this.dataModel.companyEmployeeId ? this.dataModel.companyEmployeeId : null,
                companyEmployee: this.dataModel.companyEmployee ? this.dataModel.companyEmployee : undefined,
                specified: this.specify,
            };

            deployment.specifications = this.specifications as DeploymentSpecification[];
            deployment.specified = this.specify;

            this.$emit('create:deployment', deployment);
        }

        public saveNewDeploymentProposal(): void {
            if (!this.dataModel.resourceId) {
                throw new Error('Resource is required for proposal.');
            }

            const deploymentProposal: DeploymentProposal = {
                publicComment: this.fullComment,
                date: this.dataModel.dateObject,
                shiftId: this.dataModel.shiftId,
                stateId: this.dataModel.stateId,
                resourceId: this.dataModel.resourceId,
                specified: this.specify,
                refused: false,
                deploymentId: null,
            };

            deploymentProposal.specifications = this.specify ? this.specifications as DeploymentProposalSpecification[] : undefined;

            this.$emit('create:deploymentProposal', deploymentProposal);
        }

        public saveUpdatedDeploymentProposal(): void {
            if (!this.dataModel.resourceId) {
                throw new Error('Resource is required for proposal.');
            }

            const deploymentToSave: DeploymentProposal = {
                id: this.dataModel.deploymentId!,
                publicComment: this.fullComment,
                date: this.dataModel.dateObject,
                shiftId: this.dataModel.shiftId,
                stateId: this.dataModel.stateId,
                resourceId: this.dataModel.resourceId,
                specified: this.specify,
                refused: false,
                deploymentId: null,
            };

            deploymentToSave.specifications = this.specify ? this.specifications as DeploymentProposalSpecification[] : undefined;

            if (this.isUnchangedDefaultDeployment(deploymentToSave)) {
                this.$emit('delete:deploymentProposal', deploymentToSave);
            } else {
                this.$emit('update:deploymentProposal', deploymentToSave);
            }
        }

        public isLeaveKindStillValid(leaveKind: number | undefined | null, stateNumber: number): boolean {
            if (leaveKind) {
                const leaveType: LeaveType | null = this.leaveTypeFromState(stateNumber);
                if (!leaveType) {
                    return false;
                }

                if (!this.leaveOptions[leaveType].some((val: NumberSelectInputOption): boolean => {
                    return val.value === leaveKind;
                })) {
                    return false;
                }
            }

            return true;
        }

        public verifySpecificationLeaveKinds(): void {
            for (const specification of this.specifications) {
                if (this.isProposalSpecification(specification)) {
                    if (!this.isLeaveKindStillValid(specification.leaveKindId, specification.stateId)) {
                        specification.leaveKindId = null;
                    }
                } else {
                    if (!this.isLeaveKindStillValid(specification.leaveKind, specification.stateId)) {
                        specification.leaveKind = null;
                    }
                }
            }
        }

        public changeDeploymentState(val: number): void {
            if (this.specify) {
                return;
            }

            for (const specification of this.specifications) {
                specification.stateId = val;
            }
        }

        public filterLeaveOptions(leaveKindUsed: number | undefined | null, leaveOptions: NumberSelectInputOption[]): [NullSelectInputOption, ...NumberSelectInputOption[]] {
            const filteredOptions: [NullSelectInputOption, ...NumberSelectInputOption[]] = [{label: '(automatisch)', value: null}];
            for (const leaveOption of leaveOptions) {
                for (const leaveKind of this.leaveKinds) {
                    if (leaveKind.id === leaveOption.value) {
                        if (leaveKind.shownYears.includes(Dates.Utils.getYear(this.date))) {
                            filteredOptions.push(leaveOption);
                        } else if (leaveOption.value === leaveKindUsed) {
                            filteredOptions.push({
                                ...leaveOption,
                                disabled: true,
                            });
                        }
                        break;
                    }
                }
            }

            return filteredOptions;
        }

        public sanitizePublicComment(): void {
            if (this.dataModel.publicComment === null || this.forcedPrefix === null) {
                return;
            }

            if (this.dataModel.publicComment === this.forcedPrefix) {
                this.dataModel.publicComment = null;
            } else if (this.dataModel.publicComment.substr(0, this.forcedPrefix.length + 1) === (this.forcedPrefix + ' ')) {
                this.dataModel.publicComment = this.dataModel.publicComment.substr(this.forcedPrefix.length + 1);
            }
        }

        public onChangeSpecifications(): void {
            this.validateSpecifications();
            this.verifySpecificationLeaveKinds();
        }

        public onChangeState(): void {
            this.verifySpecificationLeaveKinds();
        }

        public closeModal(): void {
            this.show = false;
        }

        public static openModal(resource: ResourceWithKeyedByDateObjects | undefined, company: Company | undefined, deployment: Deployment | DeploymentProposal | false, date: Date, shouldBeProposal: boolean = false): void {
            this.instance!.openModal(resource, company, deployment, date, shouldBeProposal);
        }
    }
</script>

<style lang="scss" scoped>
    .infoGridInformationWithoutHeader {
        grid-column: span 2;
    }

    .error-container {
        color: var(--deployment-edit-error-color);

        p, ul {
            color: var(--theme-text-color);
        }
    }

    .position-assignment-change-message {
        text-align: right;
    }

    .specifications {
        background: var(--main-background-color);
        border-radius: 0.25rem;
        padding: .5rem 1rem 1rem;
        margin-top: 1rem;
        width: 100%;

        .specificationHeader {
            display: flex;
            align-items: center;
            background: linear-gradient(180deg, var(--main-background-color) calc(100% - 0.25rem), rgb(from var(--main-background-color) r g b / 0) 100%);
            position: sticky;
            top: calc((-1 * var(--cq-card-gradient-top-size)) + 1.75rem);
            z-index: 1;

            h3 {
                flex-grow: 1;
                margin-block: 0;
            }

            .actionColumn {
                padding-block: .5rem;
            }
        }

        h3:first-child {
            margin-top: 0;
        }

        .specificationGrid {
            --base-column-count: 4;
            --leave-extra-column-count: 1;
            --projects-extra-column-count: 2;
            width: 100%;
            display: grid;
            grid-template-columns: repeat(calc(var(--base-column-count) + var(--leave-extra-column-count)), 1fr) max-content; // base = time from, time to, type, state, leave
            gap: 0 1rem;

            &.with-projects {
                grid-template-columns: repeat(calc(var(--base-column-count) + var(--leave-extra-column-count) + var(--projects-extra-column-count)), 1fr) max-content; // base + project + activity
            }

            .stateColumn:not(:has(+ .leaveColumn)) {
                // if there is leave, but it's not this row => make this column span the leave column
                grid-column: span calc(1 + var(--leave-extra-column-count));
            }

            .actionColumn {
                grid-column-end: -1;
                margin-bottom: 1rem;
                text-align: right;
                align-self: center;
            }

            > * {
                // prevent grid items from expanding their column
                min-width: 0;
            }
        }
    }
</style>
